import { ErrorBoundary, type ErrorBoundaryFallback } from '@myhelix/dd-error-tracking';
import { FC, MouseEventHandler, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Sizes, Variants } from '@common/types';
import { Box, Label } from '@base-app';

import {
  StyledContainerModal,
  StyledContent,
  StyledExitIcon,
  StyledFooter,
  StyledHeader,
  StyledIconButton,
  StyledModal,
  StyledTitle,
} from './Modal.styles';
import { ModalProps } from './Modal.types';
import { colors } from '@/common/helpers';

// A dedicated (not the shared ErrorScreen) fallback: ErrorScreen pulls from the
// @common/components barrel that Modal itself is part of, which is a circular import.
const renderNothingOnError: ErrorBoundaryFallback = () => null;

const Modal: FC<ModalProps> = ({
  children,
  css,
  footer,
  isLocked = false,
  onClose,
  show,
  size = Sizes.MEDIUM,
  title,
  titleAlignment,
  titleColor = colors.black,
  variant,
}: ModalProps) => {
  const containerRef = useRef(null);
  const iconButtonRef = useRef<HTMLButtonElement | null>(null);

  const isEscKey = (event: KeyboardEvent): boolean => event.code === 'Escape' || event.keyCode === 27;

  useEffect(() => {
    const { current } = containerRef;

    // when esc key press close modal unless locked
    const keyHandler = (e: KeyboardEvent): void => {
      if (!isLocked && isEscKey(e)) {
        onClose?.();
      }
    };

    // if the backdrop exists set up listeners
    if (current) {
      window.addEventListener('keyup', keyHandler);
    }

    // if show prop is true add inert to #root
    if (show) {
      (document?.activeElement as HTMLElement | null)?.blur();
      document?.querySelector('#root')?.setAttribute('inert', 'true');
    }

    // show/hide background scroll bar
    document.body.style.overflow = show ? 'hidden' : 'unset';

    // on unmount remove listeners
    return () => {
      document?.querySelector('#root')?.removeAttribute('inert');
      window.removeEventListener('keyup', keyHandler);
    };
  }, [show, onClose]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      if (iconButtonRef.current?.focus) {
        iconButtonRef.current.focus();
      }
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [show]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose: MouseEventHandler<HTMLButtonElement> = () => {
    onClose?.();
  };

  const modalTitle =
    typeof title === 'string' ? (
      <StyledTitle
        size={size}
        css={{ color: titleColor, textAlign: titleAlignment, display: 'flex', justifyContent: 'center' }}
      >
        {title}
      </StyledTitle>
    ) : (
      title
    );

  // This is the focus trap for the modal. This comply with the WCAG 2.1 guideline 2.4.3
  // and accessibility AA standard
  const handleFocusLastElement = (_: React.SyntheticEvent<HTMLButtonElement>): void => {
    if (show && iconButtonRef?.current?.focus) {
      iconButtonRef.current.focus();
    }
  };

  const modal = createPortal(
    <ErrorBoundary fallback={renderNothingOnError}>
      <StyledContainerModal ref={containerRef}>
        <StyledModal size={size} role="dialog" aria-modal="true" aria-labelledby="modal_id-title">
          {title !== undefined && (
            <StyledHeader id="modal-id_title">
              <Box data-testid="modal-title" css={{ flexGrow: '1' }}>
                {variant && variant === Variants.PRIMARY ? (
                  <Label
                    css={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                      marginTop: '$3',
                      fontSize: '$lg',
                    }}
                  >
                    {title}
                  </Label>
                ) : (
                  modalTitle
                )}
              </Box>
              {!isLocked && (
                <StyledIconButton
                  data-testid="modal-exit-button"
                  onClick={handleClose}
                  variant={Variants.SECONDARY}
                  ref={iconButtonRef}
                  aria-label="Close modal"
                >
                  <StyledExitIcon />
                </StyledIconButton>
              )}
            </StyledHeader>
          )}
          <StyledContent css={css}>{children}</StyledContent>
          {footer && <StyledFooter>{footer}</StyledFooter>}
          {/* This button below is dummy element in order to have a focusable element and do focus trap */}
          <button
            aria-hidden="true"
            style={{ border: 'none', background: 'transparent' }}
            onFocus={handleFocusLastElement}
          ></button>
        </StyledModal>
      </StyledContainerModal>
    </ErrorBoundary>,
    document.getElementById('modal-root') ?? document.body,
  );

  return show ? modal : null;
};

export default Modal;
